import { arrayUtils, timeUtils } from '@sato/components';
import { AxiosError } from 'axios';
import { getDay, isFuture } from 'date-fns';
import { StatusCodes } from 'http-status-codes';
import { sortBy } from 'lodash';

import { useTranslate } from '@hooks/useTranslate';
import { OwnApartment } from '@models/apartment.types';
import {
  ReservationType,
  SaunaShift,
  ScheduleShift,
  WeekdayKey,
} from '@models/sauna';
import { TranslatorFn } from '@stores/localization';
import { DayOfWeek, getDayOfWeek } from '@utils/date.utils';

export const getReservationTitle = ({
  shift,
  translationFn,
}: {
  shift: Pick<
    SaunaShift,
    'startDate' | 'weekDay' | 'startTime' | 'endTime' | 'type'
  >;
  translationFn: ReturnType<typeof useTranslate>['t'];
}) => {
  const shiftType = translationFn(
    shift.type === ReservationType.CONTINUOUS_PRIVATE_RESERVATION
      ? 'sauna.weekly_slot'
      : 'sauna.shared'
  );

  const weekdayIndex = Object.values(WeekdayKey).findIndex(
    (day) => day === shift.weekDay
  );

  const weekdayFormatted = getDayOfWeek(weekdayIndex);

  const shiftStartDateFormatted = timeUtils.formatDate({
    date: shift.startDate,
    dateFormat: timeUtils.DATE_FORMATS.DATE,
  });

  const timeRange = translationFn('time_with_at_abbreviation', {
    time: `${shift.startTime} - ${shift.endTime}`,
  });

  return arrayUtils.joinArrayString(
    [
      shiftType,
      isFuture(new Date(shift.startDate)) &&
        translationFn('starting_from', { date: shiftStartDateFormatted }),
      weekdayFormatted,
      timeRange,
    ],
    ' '
  );
};

type SaunaShiftInvalidStartDateError = { validStartDate: 'string' };

type SaunaShiftCommonError = { message: string; status: StatusCodes };

const isSaunaShiftCommonError = (
  errorData: object
): errorData is SaunaShiftCommonError =>
  'message' in errorData && 'status' in errorData;

const isSaunaShiftInvalidDateError = (
  errorData: object
): errorData is SaunaShiftInvalidStartDateError =>
  'validStartDate' in errorData;

export const getReservationErrorMessage = (error: AxiosError) => {
  const data = error.response?.data;

  if (!data || typeof data !== 'object') return 'error.sauna_shift_reservation';

  if (isSaunaShiftCommonError(data)) {
    switch (data.status) {
      case StatusCodes.FORBIDDEN:
        return 'error.sauna_shift_forbidden';
      case StatusCodes.CONFLICT:
        return 'error.sauna_overlapping_shifts';
    }
  }

  if (isSaunaShiftInvalidDateError(data)) {
    return 'error.sauna_shift_in_the_past';
  }

  return 'error.sauna_shift_reservation';
};
/** Groups shifts by weekday in a 2 dimensional array, starting from Monday (=0) and ending in Sunday (=6) */
export const groupScheduleShiftsByWeekday = (shifts: ScheduleShift[]) => {
  const groupedShifts = sortBy(
    shifts,
    (shift) => new Date(shift.startDateTime)
  ).reduce<Record<DayOfWeek, ScheduleShift[]>>(
    (acc, slot: ScheduleShift) => {
      const weekday = getDay(new Date(slot.startDateTime));

      return { ...acc, [weekday]: [...acc[weekday], slot] };
    },
    {
      [DayOfWeek.SUNDAY]: [],
      [DayOfWeek.MONDAY]: [],
      [DayOfWeek.TUESDAY]: [],
      [DayOfWeek.WEDNESDAY]: [],
      [DayOfWeek.THURSDAY]: [],
      [DayOfWeek.FRIDAY]: [],
      [DayOfWeek.SATURDAY]: [],
    }
  );

  return [
    groupedShifts[DayOfWeek.MONDAY],
    groupedShifts[DayOfWeek.TUESDAY],
    groupedShifts[DayOfWeek.WEDNESDAY],
    groupedShifts[DayOfWeek.THURSDAY],
    groupedShifts[DayOfWeek.FRIDAY],
    groupedShifts[DayOfWeek.SATURDAY],
    groupedShifts[DayOfWeek.SUNDAY],
  ];
};

export const getReservationWeekDayString = (date: Date) =>
  Object.values(WeekdayKey)[getDay(date)]!;

export const getShiftTitle = ({
  shift: { startDateTime, endDateTime },
  translationFn,
}: {
  shift: ScheduleShift;
  translationFn: TranslatorFn;
}) => {
  const startTime = timeUtils.formatDate({
    date: startDateTime,
    dateFormat: 'p',
  });
  const endTime = timeUtils.formatDate({
    date: endDateTime,
    dateFormat: 'p',
  });

  return getReservationTitle({
    shift: {
      endTime,
      startTime,
      startDate: startDateTime,
      type: ReservationType.CONTINUOUS_PRIVATE_RESERVATION,
      weekDay: getReservationWeekDayString(new Date(startDateTime)),
    },
    translationFn,
  });
};

/** Utility function for converting a `ReservableSaunaShift` to `SaunaReservation` */
export const convertShiftToReservation = (shift: ScheduleShift): SaunaShift => {
  const startTime = timeUtils.formatDate({
    date: shift.startDateTime,
    dateFormat: 'p',
  });
  const endTime = timeUtils.formatDate({
    date: shift.endDateTime,
    dateFormat: 'p',
  });

  return {
    description: null,
    name: null,
    endDate: null,
    startTime,
    endTime,
    price: 0,
    reservationId: shift.reservationId ?? '',
    resourceId: '',
    shiftLength: 0,
    startDate: shift.startDateTime,
    weekDay: getReservationWeekDayString(new Date(shift.startDateTime)),
    type: ReservationType.CONTINUOUS_PRIVATE_RESERVATION,
  };
};

/** Returns true if user is available to reserve saunas from OmaSATO */
export const hasOmaSATOReservableSaunas = (apartment: OwnApartment) =>
  !!apartment.realestate.realEstateFeatureLocations
    .filter((facility) => facility.feature === 'REAL_ESTATE_SAUNA')
    .some((sauna) => sauna.reservation?.omaSatoReservation === true);
